//#region IMPORTS
import { fromBackendDate } from "../../helpers/fromBackendDate";
import { InstoreOrder } from "../../models/instoreOrder";
import { ReactNode } from "react";
import InstoreOrderFormTrackAndTraceBadge from "./InstoreOrderFormTrackAndTraceBadge";
import InstoreOrderFormCommentBadge from "./InstoreOrderFormCommentBadge";
import MainLoadingSpinner from "../_layout/MainLoadingSpinner";
//#endregion

//#region INTERFACE
interface Props {
  orderLineItemsList: ReactNode;
  loadingSummary?: boolean;
  instoreOrder: InstoreOrder;
}
//#endregion

export default function InstoreOrderSummary({
  orderLineItemsList,
  loadingSummary = true,
  instoreOrder,
}: Props) {
  //#region RENDER
  return (
    <main className="mx-auto max-w-2xl p-6 lg:max-w-full bg-gray-50">
      <div className="lg:grid lg:grid-cols-3 lg:gap-x-12 xl:gap-x-16">
        {/* LEFT COLUMN */}
        <section>
          <h2 className="text-lg font-medium text-sky-900 mb-4">
            Order specifications
          </h2>
          <dl className="divide-y divide-gray-100">
            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">Order type</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.order_type_instore_name}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">
                Line of business
              </dt>
              <dd className="mr-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.instore_type}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">
                Store format
              </dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.store_format}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">
                Store number
              </dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.external_store_id}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">Store name</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.store_name}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">Language</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.store_language}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">
                Digital frame
              </dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.store_digital_frame
                  ? `Yes, ${instoreOrder.store_digital_frame}`
                  : "No"}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">
                Openings date
              </dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {fromBackendDate(instoreOrder.store_opening_date) || "tbc"}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">
                Delivery date
              </dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {fromBackendDate(instoreOrder.delivery_date) || "tbc"}
              </dd>
            </div>
          </dl>

          {/* EXTRA PROJECTS */}
          <h2 className="text-lg font-medium text-sky-900 sm:col-span-6 mt-12 mb-2">
            Extra projects
          </h2>
          <div className="sm:col-span-6 flex items-center flex-wrap gap-2">
            {instoreOrder?.comment !== "" &&
              instoreOrder?.comment
                ?.split(";")
                .map((value: string, i: number) => (
                  <InstoreOrderFormCommentBadge key={i} value={value} />
                ))}
          </div>

          {/* STORE INFORMATION */}
          <h2 className="text-lg font-medium text-sky-900 sm:col-span-6 mt-12 mb-2">
            Store information
          </h2>
          <dl className="divide-y divide-gray-100">
            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">
                Store number
              </dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.external_store_id}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">Store name</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.store_name}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">Language</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.store_language}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">Contact</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.store_contact_first_name || null}{" "}
                {instoreOrder.store_contact_last_name || null}
              </dd>
            </div>

            <div className="pr-4 py-1 sm:grid sm:grid-cols-3 sm:gap-2">
              <dt className="text-sm font-medium text-gray-900">Phone</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {instoreOrder.store_contact_phone || "---"}
              </dd>
            </div>
          </dl>

          <div className="sm:col-span-6 flex items-center flex-wrap gap-2 mt-6">
            <dl className="grid grid-cols-2 gap-x-24 text-sm">
              <div>
                <dt className="font-bold text-gray-900">Store address</dt>
                <dd className="mt-2 text-gray-700">
                  <address className="not-italic">
                    <span className="block">
                      {instoreOrder.store_addres_street_1}
                    </span>
                    {instoreOrder.store_addres_street_2 ? (
                      <span className="block">
                        {instoreOrder.store_addres_street_2}
                      </span>
                    ) : null}
                    {instoreOrder.store_addres_street_3 ? (
                      <span className="block">
                        {instoreOrder.store_addres_street_3}
                      </span>
                    ) : null}
                    <span className="block">
                      {instoreOrder.store_addres_zip_code}{" "}
                      {instoreOrder.store_addres_city}
                    </span>
                    <span className="block">
                      {instoreOrder.store_addres_country}
                    </span>
                  </address>
                </dd>
              </div>

              <div>
                <dt className="font-bold text-gray-900">Temporay delivery address</dt>
                <dd className="mt-2 text-gray-700">
                  <address className="not-italic">
                    <span className="block">
                      {instoreOrder.delivery_addres_street_1}
                    </span>
                    {instoreOrder.delivery_addres_street_2 ? (
                      <span className="block">
                        {instoreOrder.delivery_addres_street_2}
                      </span>
                    ) : null}
                    {instoreOrder.delivery_addres_street_3 ? (
                      <span className="block">
                        {instoreOrder.delivery_addres_street_3}
                      </span>
                    ) : null}
                    <span className="block">
                      {instoreOrder.delivery_addres_zip_code}{" "}
                      {instoreOrder.delivery_addres_city}
                    </span>
                    <span className="block">
                      {instoreOrder.delivery_addres_country}
                    </span>
                  </address>
                </dd>
              </div>
            </dl>
          </div>

          {/* TRACKING */}
          <div className="font-medium sm:col-span-6 mt-8">
            <h2 className="text-lg text-sky-900 ">Parcel tracking</h2>
            <div className="sm:col-span-4 flex items-center flex-wrap gap-2 mt-4">
              {instoreOrder?.track_and_trace !== "" &&
                instoreOrder?.track_and_trace
                  ?.split(";")
                  .map((value: string, index: number) => (
                    <InstoreOrderFormTrackAndTraceBadge
                      key={index}
                      index={index}
                      value={value}
                    />
                  ))}
            </div>
          </div>
        </section>

        {/* RIGHT COLUMN */}
        <section className="col-span-2 space-y-6">
          <h2 className="text-lg font-medium text-sky-900">Order summary</h2>
          {loadingSummary ? (
            <MainLoadingSpinner message="Loading order items..." />
          ) : (
            <div className="border border-gray-200 bg-white">
              {orderLineItemsList}
            </div>
          )}
        </section>
      </div>
    </main>
  );
  //#endregion
}
